/* ----------------------------------------------------------------------------
 * voiceSettings — the wire client for the per-workspace Aura voice config
 * (GET/PUT /v1/ai/aura/voice-settings) + a short spoken PREVIEW via the existing
 * /v1/ai/voice/tts proxy. Cookie-session + CSRF ride through the shared client;
 * NO provider key ever touches the browser.
 *
 * SERVER-AUTHORITATIVE: the persisted admin pace wins server-side, so the preview
 * flushes the current pace (saveVoicePace) BEFORE synthesising — the admin always
 * hears exactly what will be served.
 * -------------------------------------------------------------------------- */
import { api, apiRequest, USE_MOCK_DATA } from '@/lib/api';
import { clampPace, PACE_DEFAULT, type VoiceSettings } from './voicePace';

/** snake_case wire DTO (mirrors the API `{ ok, pace }` envelope). */
interface VoiceSettingsDto {
  ok?: boolean;
  pace?: number;
  // future trait keys arrive here as siblings (tone, humour, …).
}

function adapt(dto: VoiceSettingsDto | undefined, fallback: number): VoiceSettings {
  const pace = typeof dto?.pace === 'number' ? dto.pace : fallback;
  return { pace: clampPace(pace) };
}

/** GET the workspace voice config (pace; the 0.88 default when unset). */
export async function getVoiceSettings(signal?: AbortSignal): Promise<VoiceSettings> {
  if (USE_MOCK_DATA) return { pace: PACE_DEFAULT };
  const res = await api.get<VoiceSettingsDto>('/ai/aura/voice-settings', undefined, signal);
  return adapt(res, PACE_DEFAULT);
}

/** PUT (persist) the pace. Clamped client-side too; the server re-clamps + role-gates. */
export async function saveVoicePace(pace: number): Promise<VoiceSettings> {
  const clamped = clampPace(pace);
  if (USE_MOCK_DATA) return { pace: clamped };
  const res = await apiRequest<VoiceSettingsDto>('/ai/aura/voice-settings', {
    method: 'PUT',
    body: { pace: clamped },
  });
  return adapt(res, clamped);
}

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.bookedcentral.com/v1';

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Synthesise a short sample at the CURRENT server pace and return the audio Blob.
 * The server is pace-authoritative, so callers should saveVoicePace() first; a
 * `speed` hint is still sent as a harmless fallback for the no-setting case.
 */
export async function previewVoice(text: string, pace: number, signal?: AbortSignal): Promise<Blob> {
  const base = API_BASE.replace(/\/$/, '');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'audio/mpeg',
  };
  const csrf = readCookie('bc_csrf');
  if (csrf) headers['X-CSRF-Token'] = csrf;
  headers['X-Bksesh-Idempotency-Key'] = crypto.randomUUID();

  const res = await fetch(`${base}/ai/voice/tts`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ text, speed: clampPace(pace) }),
    signal,
  });
  if (!res.ok) {
    throw new Error(`voice_preview_failed_${res.status}`);
  }
  return await res.blob();
}
